import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Alert,
  Box,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  parseMgrsSimple,
  buildMgrsString,
  milsToRad,
  clamp5,
} from "../../utils/fdcHelpers";
import {
  tacticalAccordionSx,
  tacticalSummarySx,
  tacticalChipSx,
  microLabelSx,
  numericSx,
} from "../../utils/fdcTheme";

const observedSignedFromCorrection = (mode, magnitude) => {
  const meters = Number(magnitude) || 0;
  if (mode === "drop" || mode === "left" || mode === "down") return meters;
  return -meters;
};

function CorrectionAxis({ label, options, mode, onModeChange, meters, onMetersChange, accentColor = "warning.main" }) {
  return (
    <Box
      sx={(t) => ({
        p: 1,
        borderRadius: 1.25,
        border: "1px solid",
        borderColor: alpha(t.palette.divider, 0.95),
        bgcolor: t.palette.mode === "dark"
          ? alpha(t.palette.common.white, 0.02)
          : alpha(t.palette.common.black, 0.015),
      })}
    >
      <Typography sx={{ ...microLabelSx, color: "text.secondary", mb: 0.6, textAlign: "center" }}>
        {label}
      </Typography>

      <ToggleButtonGroup
        exclusive
        value={mode}
        onChange={(_e, v) => { if (v) onModeChange(v); }}
        size="small"
        fullWidth
        sx={(t) => ({
          mb: 0.75,
          "& .MuiToggleButton-root": {
            border: "1px solid",
            borderColor: alpha(t.palette.divider, 0.95),
            borderRadius: 1,
            py: 0.6,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: 0.4,
            color: t.palette.text.secondary,
            display: "flex",
            gap: 0.4,
            "&.Mui-selected": {
              bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.22 : 0.14),
              borderColor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.5 : 0.3),
              color: t.palette.warning.main,
              "&:hover": {
                bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.3 : 0.2),
              },
            },
          },
          "& .MuiToggleButton-root + .MuiToggleButton-root": { ml: 0.4 },
        })}
      >
        {options.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value}>
            {opt.icon}
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ display: "flex", alignItems: "stretch", gap: 0 }}>
        <IconButton
          size="small"
          onClick={() => onMetersChange(Math.max(0, Number(meters) - 10))}
          sx={(t) => ({
            border: "1px solid",
            borderColor: alpha(t.palette.divider, 0.95),
            borderRight: 0,
            borderRadius: "6px 0 0 6px",
            px: 1,
          })}
        >
          <RemoveRoundedIcon fontSize="small" />
        </IconButton>
        <TextField
          type="number"
          value={meters}
          onChange={(e) => onMetersChange(Number(e.target.value))}
          size="small"
          inputProps={{ min: 0, step: 10, style: { textAlign: "center" } }}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              height: 38,
            },
            "& fieldset": { borderLeftWidth: 0, borderRightWidth: 0 },
            "& input": {
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              fontSize: 16,
              py: 0,
            },
          }}
          InputProps={{
            endAdornment: (
              <Typography sx={{ ...microLabelSx, color: "text.secondary", mr: 0.5 }}>m</Typography>
            ),
          }}
        />
        <IconButton
          size="small"
          onClick={() => onMetersChange(Number(meters) + 10)}
          sx={(t) => ({
            border: "1px solid",
            borderColor: alpha(t.palette.divider, 0.95),
            borderLeft: 0,
            borderRadius: "0 6px 6px 0",
            px: 1,
          })}
        >
          <AddRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function ManualObservationPanel({
  expanded,
  onChange,
  hasMission,
  form,
  backendUrl,
  onSubmitted,
}) {
  const [bearingMils, setBearingMils] = useState(0);
  const [dropAddMode, setDropAddMode] = useState("drop");
  const [dropAddMeters, setDropAddMeters] = useState(0);
  const [lateralMode, setLateralMode] = useState("left");
  const [lateralMeters, setLateralMeters] = useState(0);
  const [verticalMode, setVerticalMode] = useState("up");
  const [verticalMeters, setVerticalMeters] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // 30-second cooldown after a successful submit, so a double-tap can't fire two
  // copies of the same observation. `cooldownUntil` is the earliest epoch ms a new
  // submission is allowed; `now` ticks every second to drive the countdown text.
  const COOLDOWN_MS = 30_000;
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [cooldownUntil]);
  const cooldownRemaining = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const inCooldown = cooldownRemaining > 0;

  const preview = useMemo(() => {
    const parsed = parseMgrsSimple(form?.mgrs_target);
    const targetAltitude = Number(form?.target_altitude);

    if (!parsed) return { ok: false, message: "Set Target MGRS in Mission first." };
    if (!Number.isFinite(targetAltitude)) return { ok: false, message: "Target altitude missing." };

    const observedAlong = observedSignedFromCorrection(dropAddMode, dropAddMeters);
    const observedLateral = observedSignedFromCorrection(lateralMode, lateralMeters);
    const observedVertical = observedSignedFromCorrection(verticalMode, verticalMeters);
    const angle = milsToRad(Number(bearingMils) || 0);

    const alongEast = Math.sin(angle);
    const alongNorth = Math.cos(angle);
    const rightEast = Math.cos(angle);
    const rightNorth = -Math.sin(angle);

    const observedX = parsed.e + observedAlong * alongEast + observedLateral * rightEast;
    const observedY = parsed.n + observedAlong * alongNorth + observedLateral * rightNorth;
    const observedZ = targetAltitude + observedVertical;

    const e5 = clamp5(observedX);
    const n5 = clamp5(observedY);

    if (e5 === null || n5 === null) {
      return { ok: false, message: "Computed observation outside MGRS grid bounds." };
    }

    return {
      ok: true,
      observedAlong, observedLateral, observedVertical,
      observedX, observedY, observedZ,
      mgrs: buildMgrsString(parsed.zone, e5, n5),
    };
  }, [bearingMils, dropAddMode, dropAddMeters, lateralMode, lateralMeters, verticalMode, verticalMeters, form?.mgrs_target, form?.target_altitude]);

  const handleSubmit = async () => {
    setError(""); setSuccessMessage("");
    if (!hasMission || !form?.mission_id) { setError("Select a mission first."); return; }
    if (!preview.ok) { setError(preview.message || "Cannot compute observation."); return; }
    if (Date.now() < cooldownUntil) {
      const s = Math.max(1, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setError(`Wait ${s}s before sending another observation (anti-double-tap).`);
      return;
    }

    const payload = {
      mission_id: form.mission_id,
      bearing: Number(bearingMils) || 0,
      drop_add: preview.observedAlong,
      left_right: preview.observedLateral,
      up_down: preview.observedVertical,
      observed_x: Number(preview.observedX.toFixed(3)),
      observed_y: Number(preview.observedY.toFixed(3)),
      observed_z: Number(preview.observedZ.toFixed(3)),
    };

    try {
      setSubmitting(true);
      const response = await axios.post(`${backendUrl}/submit-observation`, payload, { timeout: 12000 });
      if (response.data?.status !== "success") {
        throw new Error(response.data?.message || "Failed to submit observation.");
      }
      setSuccessMessage(`Obs ${response.data?.shot_id ?? ""} sent`);
      // Start the anti-double-tap cooldown only after a successful submit.
      setCooldownUntil(Date.now() + COOLDOWN_MS);
      setNow(Date.now());
      // Reset for next correction
      setDropAddMeters(0);
      setLateralMeters(0);
      setVerticalMeters(0);
      if (typeof onSubmitted === "function") {
        await onSubmitted(response.data, payload, preview);
      }
    } catch (submitError) {
      // If the backend rejected with a server-side cooldown (429), propagate the
      // remaining wait time into the frontend cooldown so the button shows a
      // matching countdown.
      const status = submitError.response?.status;
      const retry = Number(submitError.response?.data?.retry_after_s);
      if (status === 429 && Number.isFinite(retry) && retry > 0) {
        const until = Date.now() + retry * 1000;
        setCooldownUntil(until);
        setNow(Date.now());
      }
      setError(submitError.response?.data?.message || submitError.message || "Failed to submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const targetReady = Boolean(parseMgrsSimple(form?.mgrs_target));
  const hasAnyCorrection = dropAddMeters > 0 || lateralMeters > 0 || verticalMeters > 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disabled={!hasMission}
      disableGutters
      sx={(theme) => tacticalAccordionSx(theme)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={(theme) => tacticalSummarySx(theme, "warning")}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Manual Correction
          </Typography>
          <Chip
            size="small"
            label={targetReady ? "Target locked" : "Target required"}
            sx={(theme) => tacticalChipSx(theme, targetReady ? "success" : "warning")}
          />
          {hasAnyCorrection && targetReady && (
            <Chip
              size="small"
              label="Pending correction"
              sx={(theme) => tacticalChipSx(theme, "warning")}
            />
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 1.25, sm: 1.5 }, pb: 1.25, pt: 1 }}>
        <Stack spacing={1.25}>
          {!targetReady && (
            <Alert severity="warning" sx={{ fontWeight: 600, py: 0.5 }}>
              Set Target MGRS and altitude in Mission first.
            </Alert>
          )}

          {/* FO Azimuth */}
          <Box
            sx={(t) => ({
              p: 1,
              borderRadius: 1.25,
              border: "1px solid",
              borderColor: alpha(t.palette.divider, 0.95),
              bgcolor: t.palette.mode === "dark"
                ? alpha(t.palette.common.white, 0.02)
                : alpha(t.palette.common.black, 0.015),
              display: "flex",
              alignItems: "center",
              gap: 1,
            })}
          >
            <ExploreRoundedIcon sx={{ color: "text.secondary", fontSize: 22 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ ...microLabelSx, color: "text.secondary", lineHeight: 1.1 }}>
                FO → Target azimuth
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10.5 }}>
                Direction the observer is facing toward the target (mils)
              </Typography>
            </Box>
            <TextField
              type="number"
              value={bearingMils}
              onChange={(e) => setBearingMils(e.target.value)}
              size="small"
              inputProps={{ min: 0, max: 6400, step: 50, style: { textAlign: "center" } }}
              sx={{
                width: 110,
                "& .MuiOutlinedInput-root": { borderRadius: 1, height: 40 },
                "& input": {
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 700,
                  fontSize: 16,
                },
              }}
              InputProps={{
                endAdornment: (
                  <Typography sx={{ ...microLabelSx, color: "text.secondary", ml: 0.3, mr: 0.5 }}>mils</Typography>
                ),
              }}
            />
          </Box>

          {/* Three correction axes */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 1,
            }}
          >
            <CorrectionAxis
              label="Range"
              mode={dropAddMode}
              onModeChange={setDropAddMode}
              meters={dropAddMeters}
              onMetersChange={setDropAddMeters}
              options={[
                { value: "drop", label: "Drop", icon: <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} /> },
                { value: "add", label: "Add", icon: <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} /> },
              ]}
            />
            <CorrectionAxis
              label="Lateral"
              mode={lateralMode}
              onModeChange={setLateralMode}
              meters={lateralMeters}
              onMetersChange={setLateralMeters}
              options={[
                { value: "left", label: "Left", icon: <ArrowBackRoundedIcon sx={{ fontSize: 16 }} /> },
                { value: "right", label: "Right", icon: <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} /> },
              ]}
            />
            <CorrectionAxis
              label="Vertical"
              mode={verticalMode}
              onModeChange={setVerticalMode}
              meters={verticalMeters}
              onMetersChange={setVerticalMeters}
              options={[
                { value: "down", label: "Down", icon: <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} /> },
                { value: "up", label: "Up", icon: <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} /> },
              ]}
            />
          </Box>

          {/* Live preview */}
          {preview.ok && (
            <Box
              sx={(t) => ({
                p: 1,
                borderRadius: 1.25,
                border: "1px solid",
                borderColor: alpha(t.palette.warning.main, 0.3),
                bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.06 : 0.04),
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                gap: 1,
                alignItems: "center",
              })}
            >
              <Box>
                <Typography sx={{ ...microLabelSx, color: "text.secondary" }}>
                  Observed MGRS
                </Typography>
                <Typography sx={{ ...numericSx, fontSize: 13.5 }}>{preview.mgrs}</Typography>
              </Box>
              <Box>
                <Typography sx={{ ...microLabelSx, color: "text.secondary" }}>Range Δ</Typography>
                <Typography sx={{ ...numericSx, fontSize: 14, color: preview.observedAlong < 0 ? "error.main" : preview.observedAlong > 0 ? "success.main" : "text.primary" }}>
                  {preview.observedAlong > 0 ? "+" : ""}{Math.round(preview.observedAlong)} m
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ ...microLabelSx, color: "text.secondary" }}>Lateral Δ</Typography>
                <Typography sx={{ ...numericSx, fontSize: 14, color: preview.observedLateral < 0 ? "error.main" : preview.observedLateral > 0 ? "success.main" : "text.primary" }}>
                  {preview.observedLateral > 0 ? "+" : ""}{Math.round(preview.observedLateral)} m
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ ...microLabelSx, color: "text.secondary" }}>Vertical Δ</Typography>
                <Typography sx={{ ...numericSx, fontSize: 14, color: preview.observedVertical < 0 ? "error.main" : preview.observedVertical > 0 ? "success.main" : "text.primary" }}>
                  {preview.observedVertical > 0 ? "+" : ""}{Math.round(preview.observedVertical)} m
                </Typography>
              </Box>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
          {successMessage && <Alert severity="success" sx={{ py: 0.5 }}>{successMessage}</Alert>}

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SendRoundedIcon />}
              onClick={handleSubmit}
              disabled={!preview.ok || submitting || !hasMission || !hasAnyCorrection || inCooldown}
              sx={(t) => ({
                minHeight: 44,
                minWidth: 220,
                px: 2.5,
                borderRadius: 1.25,
                fontWeight: 700,
                fontSize: 13.5,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: t.palette.mode === "dark" ? "#11150f" : "#f7f7f2",
                bgcolor: t.palette.mode === "dark" ? "#a6b078" : "#556b2f",
                "&:hover": {
                  bgcolor: t.palette.mode === "dark" ? "#b7c58a" : "#465a27",
                },
                "&.Mui-disabled": {
                  color: t.palette.text.disabled,
                  bgcolor: alpha(t.palette.action.disabled, 0.12),
                },
              })}
            >
              {submitting
                ? "Submitting…"
                : inCooldown
                  ? `Wait ${cooldownRemaining}s…`
                  : "Submit observation"}
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
